import { useState, useEffect } from "react";
import type { PropertyTemplate } from "../types/property";
import * as propertyService from "../lib/services/propertyService";
import { SlimPropertyTemplate } from "../lib/services/propertyService";

type UsePropertiesResult = {
  properties: PropertyTemplate[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

export function useProperties(): UsePropertiesResult {
  const [properties, setProperties] = useState<PropertyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await propertyService.fetchAllProperties();
      setProperties(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load properties",
      );
      console.error("[useProperties] Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { properties, loading, error, refetch: fetchData };
}

export function useFeaturedProperties(): UsePropertiesResult {
  const [properties, setProperties] = useState<PropertyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await propertyService.fetchFeaturedProperties();
      setProperties(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load featured properties",
      );
      console.error("[useFeaturedProperties] Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { properties, loading, error, refetch: fetchData };
}

export function useGalleryProperties(): UsePropertiesResult {
  const [properties, setProperties] = useState<PropertyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await propertyService.fetchGalleryProperties();
      setProperties(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load gallery properties",
      );
      console.error("[useGalleryProperties] Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { properties, loading, error, refetch: fetchData };
}

export function useTemplateProperties(): UsePropertiesResult {
  const [properties, setProperties] = useState<PropertyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await propertyService.fetchTemplateProperties();
      setProperties(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load template properties",
      );
      console.error("[useTemplateProperties] Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { properties, loading, error, refetch: fetchData };
}

export function useProperty(id: string | undefined): {
  property: PropertyTemplate | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [property, setProperty] = useState<PropertyTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await propertyService.fetchPropertyById(id);
      setProperty(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load property");
      console.error("[useProperty] Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  return { property, loading, error, refetch: fetchData };
}

type UseSlimTemplatePropertiesResult = {
  templates: SlimPropertyTemplate[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

export function useSlimTemplateProperties(): UseSlimTemplatePropertiesResult {
  const [templates, setTemplates] = useState<SlimPropertyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await propertyService.fetchSlimTemplateProperties();
      setTemplates(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load template properties",
      );
      console.error("[useSlimTemplateProperties] Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { templates, loading, error, refetch: fetchData };
}
